<div class="eco-page">
  <header class="eco-header flex align-items-center justify-content-between">
    <div class="flex align-items-center gap-3">
      <div class="eco-brand-badge">
        <img src="assets/ecodados-symbol.svg" alt="EcoDados Logo">
      </div>
      <h1 class="eco-title text-lg my-0">EcoDados <span class="font-normal text-600">| Gráficos</span></h1>
    </div>

    <div class="flex align-items-center gap-2">
      <nav class="hidden md:flex eco-nav">
        <a routerLink="/coletas" routerLinkActive="active" class="eco-nav-link">Minhas Coletas</a>
        @if (role() === 'ADMIN') {
          <a routerLink="/indicadores" routerLinkActive="active" class="eco-nav-link">Painel Admin</a>
          <a routerLink="/analytics" routerLinkActive="active" class="eco-nav-link">Gráficos</a>
        }
      </nav>

      <p-button icon="pi pi-sign-out" [text]="true" [rounded]="true" severity="secondary" (onClick)="logout()" pTooltip="Sair do sistema" styleClass="hidden md:inline-flex" />

      <!-- Botão Menu Mobile -->
      <p-button icon="pi pi-bars" [text]="true" [rounded]="true" severity="secondary" styleClass="md:hidden" (onClick)="showMobileMenu.set(true)" aria-label="Abrir menu de navegação" />
    </div>
  </header>

  <main class="eco-container flex flex-column gap-4">
    <!-- Section Header -->
    <div class="pt-1">
      <h2 class="eco-title text-xl my-0">Visão Geral</h2>
      <p class="eco-meta mt-1 mb-0">Acompanhe os principais indicadores e tendências ambientais.</p>
    </div>

    <!-- Summary Tiles — side by side on all breakpoints -->
    <section class="grid">
      <div class="col-6 sm:col-6 lg:col-3">
        <div class="eco-card flex flex-column justify-content-between h-full">
          <span class="eco-label">Métricas Ativas</span>
          <div class="flex align-items-end justify-content-between mt-2">
            <span class="eco-kpi-value text-teal-800">{{ summary().activeIndicators }}</span>
            <span class="material-symbols-outlined text-teal-200 text-3xl">analytics</span>
          </div>
        </div>
      </div>
      
      <div class="col-6 sm:col-6 lg:col-3">
        <div class="eco-card flex flex-column justify-content-between h-full">
          <span class="eco-label">Pendências Globais</span>
          <div class="flex align-items-end justify-content-between mt-2">
            <span class="eco-kpi-value text-teal-700">{{ summary().globalPendingTasks }}</span>
            <span class="material-symbols-outlined text-teal-200 text-3xl">pending_actions</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Dashboard Tabs & Actions — consistent eco-toolbar -->
    <div class="flex flex-column sm:flex-row sm:align-items-center justify-content-between gap-3">
      <div class="eco-toolbar">
        <button (click)="activeTab.set('trends')" 
          class="eco-tab-button"
          [class.active]="activeTab() === 'trends'">
          Evolução e Tendências
        </button>
        <button (click)="activeTab.set('responses')" 
          class="eco-tab-button"
          [class.active]="activeTab() === 'responses'">
          Histórico de Respostas
        </button>
      </div>

      <p-button 
        label="Exportar (.xlsx)" 
        icon="pi pi-download" 
        severity="secondary" 
        [outlined]="true"
        size="small" 
        [rounded]="true" 
        styleClass="w-full sm:w-auto"
        [loading]="dataTransferService.exporting()" 
        (onClick)="exportXlsx()" />
    </div>

    @if (activeTab() === 'trends') {
      <!-- Chart Section -->
      <section class="eco-card flex flex-column gap-4">
        <div class="flex flex-column md:flex-row md:align-items-center justify-content-between gap-3">
          <div>
            <h2 class="eco-title text-lg my-0">Evolução Histórica</h2>
            <p class="eco-meta my-0 mt-1">Selecione um indicador para visualizar a tendência temporal.</p>
          </div>

          <div class="flex align-items-center gap-3 min-w-0">
            <div class="relative w-full md:w-auto" style="min-width: 200px; max-width: 320px;">
              <select [ngModel]="selectedIndicatorId()" (ngModelChange)="selectedIndicatorId.set($event)"
                class="eco-input" style="text-overflow: ellipsis;">
                @for (item of indicators(); track item.id) {
                  <option [value]="item.id">{{ item.sigla }} - {{ item.nome }}</option>
                }
              </select>
            </div>
          </div>
        </div>

        <div class="relative h-25rem w-full eco-card-low p-2 sm:p-3">
          @if (loading()) {
            <div class="absolute inset-0 flex flex-column align-items-center justify-center bg-white-alpha-60 backdrop-blur-sm z-2 border-round-2xl">
              <div class="w-3rem h-3rem border-circle border-3 border-teal-200 border-t-teal-700 eco-spin"></div>
              <p class="mt-3 eco-label text-teal-800">Processando dados...</p>
            </div>
          }
          <canvas #chartCanvas></canvas>
        </div>
      </section>
    } @else {
      <!-- Responses Table Section -->
      <section class="eco-card flex flex-column gap-4">
        <div>
          <h2 class="eco-title text-lg my-0">Todas as Respostas</h2>
          <p class="eco-meta my-0 mt-1">Cronologia completa de métricas informadas pelos responsáveis.</p>
        </div>

        <div class="overflow-auto border-round-xl border-1 border-teal-100">
          <table class="w-full text-left border-collapse" style="min-width: 640px;">
            <thead>
              <tr class="bg-teal-50 border-b-1 border-teal-100">
                <th class="p-3 eco-label">Indicador</th>
                <th class="p-3 eco-label">Mês Referência</th>
                <th class="p-3 eco-label">Responsável</th>
                <th class="p-3 eco-label text-right">Valor</th>
                <th class="p-3 eco-label">Preenchido em</th>
              </tr>
            </thead>
            <tbody>
              @for (row of allResponses(); track row.id) {
                <tr class="border-b-1 border-teal-50 hover:bg-teal-50">
                  <td class="p-3">
                    <div class="flex flex-column gap-1">
                      <span class="eco-label text-teal-700">{{ row.indicadores.sigla }}</span>
                      <span class="text-sm font-medium text-800">{{ row.indicadores.nome }}</span>
                    </div>
                  </td>
                  <td class="p-3">
                    <div class="flex align-items-center gap-2">
                      <span class="material-symbols-outlined text-sm text-teal-700">calendar_today</span>
                      <span class="eco-meta font-medium">{{ row.mes_ano | date:'MM/yyyy':'UTC' }}</span>
                    </div>
                  </td>
                  <td class="p-3">
                    <div class="flex align-items-center gap-2">
                      <span class="material-symbols-outlined text-sm opacity-50">person</span>
                      <div class="flex flex-column">
                        <span class="eco-meta font-medium text-800">{{ row.usuario_id || 'Não informado' }}</span>
                      </div>
                    </div>
                  </td>
                  <td class="p-3 text-right">
                    <span class="text-sm font-bold text-teal-800">{{ row.valor_informado | number:'1.0-2' }}</span>
                    <span class="eco-meta font-medium ml-1">{{ row.indicadores.unidade_medida }}</span>
                  </td>
                  <td class="p-3">
                    <div class="flex align-items-center gap-2 text-600">
                      <span class="material-symbols-outlined text-sm">schedule</span>
                      <span class="eco-meta font-medium">{{ row.data_preenchimento | date:'dd/MM/yyyy HH:mm' }}</span>
                    </div>
                  </td>
                </tr>
              } @empty {
                <tr>
                  <td colspan="5" class="p-6 text-center">
                    <div class="flex flex-column align-items-center opacity-40 gap-2">
                      <span class="material-symbols-outlined text-5xl">folder_off</span>
                      <p class="eco-label my-0">Nenhum dado encontrado</p>
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </section>
    }
  </main>

  <!-- Menu Drawer Mobile -->
  <p-drawer 
    [(visible)]="showMobileMenu" 
    position="right" 
    [style]="{ width: '280px', maxWidth: '85vw' }"
    [modal]="true">
    <ng-template #header>
      <div class="flex align-items-center gap-2">
        <div class="eco-brand-badge" style="width: 38px; height: 38px; min-width: 38px; min-height: 38px;">
          <img src="assets/ecodados-symbol.svg" alt="EcoDados Logo" style="width: 24px; height: 24px;">
        </div>
        <div class="flex flex-column">
          <span class="font-bold text-sm text-800">EcoDados</span>
          <span class="eco-meta">Navegação</span>
        </div>
      </div>
    </ng-template>

    <div class="flex flex-column justify-content-between h-full pt-3 pb-2">
      <div class="flex flex-column gap-2">
        <a routerLink="/coletas" routerLinkActive="active" (click)="showMobileMenu.set(false)" class="eco-mobile-nav-link">
          <span class="material-symbols-outlined text-xl text-teal-700">task_alt</span>
          <span>Minhas Coletas</span>
        </a>

        @if (role() === 'ADMIN') {
          <a routerLink="/indicadores" routerLinkActive="active" (click)="showMobileMenu.set(false)" class="eco-mobile-nav-link">
            <span class="material-symbols-outlined text-xl text-teal-700">tune</span>
            <span>Painel Admin</span>
          </a>

          <a routerLink="/analytics" routerLinkActive="active" (click)="showMobileMenu.set(false)" class="eco-mobile-nav-link">
            <span class="material-symbols-outlined text-xl text-teal-700">analytics</span>
            <span>Gráficos</span>
          </a>
        }
      </div>

      <!-- Rodapé do menu mobile -->
      <div class="border-t-1 border-teal-100 pt-3 flex flex-column gap-2">
        <div class="px-2 py-1 flex flex-column">
          <span class="text-xs font-bold text-800">{{ authService.currentUser()?.name }}</span>
          <span class="eco-meta">{{ role() === 'ADMIN' ? 'Administrador' : 'Responsável' }}</span>
        </div>
        <button (click)="logout()" class="eco-mobile-nav-link text-red-700 hover:bg-red-50 cursor-pointer border-none bg-transparent w-full text-left">
          <span class="material-symbols-outlined text-xl text-red-600">logout</span>
          <span>Sair do sistema</span>
        </button>
      </div>
    </div>
  </p-drawer>
</div>
